import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from './Button';

interface ModalProps {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}

export function Modal({ title, onClose, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Callers rebuild onClose every render; the open effect must not re-run and steal focus back.
  const latestClose = useRef(onClose);
  useEffect(() => {
    latestClose.current = onClose;
  });

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') latestClose.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = restoreOverflow;
      opener?.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div aria-hidden="true" className="fixed inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative mx-auto my-8 w-full max-w-4xl px-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="rounded-lg border border-slate-200 bg-white shadow-xl focus:outline-none"
        >
          <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <h2 id={titleId} className="text-base font-semibold">
              {title}
            </h2>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </header>
          <div className="px-5 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}
